import { useQueryClient } from '@tanstack/react-query';
import { useFormik } from 'formik';
import { useCallback, useDeferredValue, useEffect, useMemo, useRef, useState } from 'react';
import { generatePath, useNavigate } from 'react-router-dom';
import { useDebounce } from 'react-use';
import { toast } from 'sonner';
import * as Yup from 'yup';

import { ARBITRAGE_QUERY_KEYS } from '@/entities/arbitrage';
import { fetchAllProjectsWithPairs } from '@/entities/projects-data/api/fetchAllProjectsWithPairs';
import { syncProjectsToRedux } from '@/entities/projects-data/lib/syncToRedux';
import { PROJECTS_DATA_QUERY_KEYS } from '@/entities/projects-data/model/projectsDataQueryKeys';
import { WALLET_BALANCER_QUERY_KEYS } from '@/entities/wallet-balancer';

import { ApiCexPairs, ApiPairs } from 'api';
import { canCreatePair, IDex } from 'api/apiDictionary/models';
import type { IDescribedDexPair } from 'api/apiPairs/models';
import { addDexPair } from 'entities/project/api/project/api';
import { PancakeInfiniteExtra, usePancakePoolData } from 'features/add-pancake-infinite-pair';
import { UNISWAP_FEES } from 'shared/consts/fees';
import { ARBITRAGE_CONFIGS_RELOAD, BALANCER_TASKS_RELOAD } from 'shared/consts/reload';
import { ROUTE_PATHS } from 'shared/consts/routes';
import { useTypedDispatch, useTypedSelector } from 'store';
import { getCexs } from 'store/slices/dictionary';
import { addNewPairToProject } from 'store/slices/projects';
import { dropAlertState, setAlertState } from 'store/slices/ui';
import { Bus } from 'tools';
import { ICexPair } from 'types/pairs';
import { isAddress } from 'utils';
import { EExchange } from 'web3';

import { IForm } from './models';

const useAddPairModal = ({
  projectId,
  onClose,
  onOpen,
  tradingType,
  dex,
}: {
  projectId: number;
  onClose: () => void;
  onOpen: () => void;
  tradingType: EExchange;
  dex?: IDex;
}) => {
  const navigate = useNavigate();
  const dispatch = useTypedDispatch();
  const queryClient = useQueryClient();
  const cexs = useTypedSelector(store => store.dictionary.cexs);
  const pairCexs = useMemo(() => cexs?.filter(canCreatePair), [cexs]);
  const [edited, setEdited] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [resolvedPool, setResolvedPool] = useState<IDescribedDexPair | null>(null);
  const [poolResolveLoading, setPoolResolveLoading] = useState(false);
  const [poolResolveError, setPoolResolveError] = useState<string>();

  const isPancakeInfinite = dex?.dex === 'pancakeswap_inf_cl:bsc';
  const pancakeExtraRef = useRef<PancakeInfiniteExtra | null | undefined>(null);
  const poolResolveRequest = useRef(0);

  const validationSchema = useMemo(
    () =>
      Yup.object({
        notes: Yup.string().max(100, 'Notes field is too long'),
        fee: Yup.number(),
        cexs: Yup.array()
          .of(Yup.object())
          .when([], {
            is: () => tradingType === EExchange.cex,
            then: schema => schema.min(1, 'Select at least one exchange'),
            otherwise: schema => schema,
          }),
        baseToken: dex
          ? Yup.string().test(
              'baseToken',
              'Enter valid token address or fill pair address',
              (v: string | undefined) => isAddress(v),
            )
          : Yup.string().required('Enter token symbol'),
        quoteToken: dex
          ? Yup.string().test(
              'baseToken',
              'Enter valid token address or fill pair address',
              (v: string | undefined) => isAddress(v),
            )
          : Yup.string().required('Enter token symbol'),
        pairAddress: dex
          ? Yup.string().when(['baseToken', 'quoteToken'], {
              is: (baseToken: string, quoteToken: string) => baseToken === '' && quoteToken === '',
              then: schema =>
                schema.test('pairAddress', 'Enter valid token address', (v: string | undefined) =>
                  isAddress(v),
                ),
            })
          : Yup.string(),
        poolId: isPancakeInfinite
          ? Yup.string()
              .required('Pool ID is required for Pancake Infinite')
              .matches(
                /^0x[0-9a-fA-F]{64}$/,
                'Pool ID must be a valid hex string (0x + 64 hex chars)',
              )
          : Yup.string(),
      }),
    [dex, isPancakeInfinite, tradingType],
  );

  const INITIAL_VALUES = useMemo<IForm>(
    () => ({
      notes: '',
      cexs:
        tradingType === EExchange.cex && pairCexs && pairCexs.length !== 0
          ? [{ ...pairCexs[0], id: pairCexs[0].cex }]
          : [],
      fee: UNISWAP_FEES[0].value,
      pairAddress: '',
      baseToken: '',
      quoteToken: '',
      poolId: '',
    }),
    [tradingType, pairCexs],
  );

  const refreshProjectCaches = useCallback(async () => {
    try {
      await queryClient.invalidateQueries({
        queryKey: WALLET_BALANCER_QUERY_KEYS.list({ projectId, limit: 1000, offset: 0 }),
      });

      queryClient.invalidateQueries({ queryKey: PROJECTS_DATA_QUERY_KEYS.all });
      queryClient.invalidateQueries({ queryKey: ARBITRAGE_QUERY_KEYS.all });
      queryClient.invalidateQueries({ queryKey: WALLET_BALANCER_QUERY_KEYS.all });

      const updatedAt = Date.now();
      const projectsData = await fetchAllProjectsWithPairs();
      syncProjectsToRedux(dispatch, projectsData);
      queryClient.setQueryData(PROJECTS_DATA_QUERY_KEYS.list(), projectsData, { updatedAt });

      await queryClient.refetchQueries({
        queryKey: WALLET_BALANCER_QUERY_KEYS.list({ projectId, limit: 1000, offset: 0 }),
      });
    } catch (error) {
      console.error('[useAddPairModal] Failed to refresh caches', error);
    } finally {
      Bus.emit(ARBITRAGE_CONFIGS_RELOAD, projectId);
      Bus.emit(BALANCER_TASKS_RELOAD, projectId);
    }
  }, [dispatch, projectId, queryClient]);

  const addPair = useCallback(
    async ({
      cexs: selectedCexs,
      fee,
      notes,
      baseToken,
      quoteToken,
      poolId,
      pairAddress,
    }: IForm): Promise<void> => {
      setLoading(true);

      const _dex = dex ? dex.dex : 'uniswap_v2';

      const _fee =
        dex?.dex === 'uniswap_v3' ||
        dex?.dex === 'uniswap_v3:arbitrum_one' ||
        dex?.dex === 'uniswap_v3:polygon' ||
        dex?.dex === 'uniswap_v3:base' ||
        dex?.dex === 'uniswap_v3:robinhood' ||
        dex?.dex === 'pancakeswap_v3:bsc' ||
        dex?.dex === 'pancakeswap_v3:robinhood' ||
        dex?.dex === 'sushiswap_v3:haqq' ||
        dex?.dex === 'aerodrome_v3:base' ||
        dex?.dex === 'aerodrome_v3_2:base'
          ? fee
          : undefined;

      // Use new endpoint for Pancake Infinite CL
      if (isPancakeInfinite) {
        // Enforce that Pool ID is validated and pancakeExtra is available
        if (!poolId || !pancakeExtraRef.current) {
          setLoading(false);
          dispatch(
            setAlertState({
              type: 'failed-img',
              text: 'Pool ID is required and must be valid for Pancake Infinite pairs',
              onClose: () => dispatch(dropAlertState()),
              onSubmit: () => {
                dispatch(dropAlertState());
              },
            }),
          );
          return;
        }

        const response = await addDexPair({
          projectId,
          dex: _dex,
          fee: pancakeExtraRef.current.fee,
          notes,
          token_base: baseToken,
          token_quote: quoteToken,
          pool_id: poolId,
          extra: pancakeExtraRef.current,
        });

        if (!response.isSuccess) {
          onClose();
          setLoading(false);
          dispatch(
            setAlertState({
              type: 'failed-img',
              text: response.errorMessage || 'Failed to create DEX pair',
              onClose: () => dispatch(dropAlertState()),
              onSubmit: () => {
                dispatch(dropAlertState());
                onOpen();
              },
            }),
          );
          return;
        }

        // Fetch created pair data
        const { isSuccess, data: pairData } = await ApiPairs.getDexPairById(
          Number(response.data.id),
        );

        onClose();
        setLoading(false);

        if (isSuccess) {
          dispatch(
            addNewPairToProject({
              pair: pairData,
              type: tradingType,
            }),
          );
          dispatch(
            setAlertState({
              type: 'success-img',
              text: `Created new pair: ${pairData.symbol}`,
              onClose: () => dispatch(dropAlertState()),
              onSubmit: () => {
                dispatch(dropAlertState());
              },
            }),
          );
          void refreshProjectCaches();
          navigate(
            generatePath(ROUTE_PATHS.dexPair, {
              projectId: projectId.toString(),
              pairId: pairData.id.toString(),
            }),
          );
        }

        return;
      }

      if (tradingType === EExchange.cex) {
        const cexSelection = selectedCexs ?? [];

        if (cexSelection.length === 0) {
          setLoading(false);
          toast.error('Select at least one exchange');
          return;
        }

        const createResults = await Promise.allSettled(
          cexSelection.map(({ cex }) =>
            ApiPairs.addPair({
              id: projectId,
              dex: _dex,
              cex,
              tradingType,
              fee: _fee,
              notes,
              token_base: baseToken,
              token_quote: quoteToken,
              swap_gas_limit: 1000000,
              pool_id: isAddress(pairAddress) ? pairAddress : undefined,
              pair: isAddress(pairAddress) ? pairAddress : undefined,
            }),
          ),
        );

        const createdIds: number[] = [];
        const failedMessages: string[] = [];

        createResults.forEach((result, index) => {
          const selectedCex = cexSelection[index];
          const cexLabel = selectedCex?.description ?? selectedCex?.cex ?? 'Unknown';

          if (result.status === 'fulfilled' && result.value.isSuccess) {
            createdIds.push(Number(result.value.data.id));
            return;
          }

          const errorMessage =
            result.status === 'fulfilled' ? result.value.errorMessage : 'Request failed';

          failedMessages.push(`${cexLabel}: ${errorMessage || 'Failed to create pair'}`);
        });

        const fetchResults = await Promise.allSettled(
          createdIds.map(id => ApiCexPairs.getCexPairById(Number(id))),
        );

        const createdPairs: ICexPair[] = [];

        fetchResults.forEach((result, index) => {
          if (result.status === 'fulfilled' && result.value.isSuccess) {
            createdPairs.push(result.value.data);
            return;
          }

          failedMessages.push(`Pair #${createdIds[index]}: Failed to fetch pair`);
        });

        createdPairs.forEach(pair => {
          dispatch(
            addNewPairToProject({
              pair,
              type: tradingType,
            }),
          );
        });

        if (createdPairs.length > 0) {
          toast.success(
            createdPairs.length === 1
              ? `Created new pair: ${createdPairs[0].symbol}`
              : `Created ${createdPairs.length} pairs`,
          );
          void refreshProjectCaches();
        }

        if (failedMessages.length > 0) {
          const errorPreview = failedMessages.slice(0, 2).join('; ');
          const errorSuffix = failedMessages.length > 2 ? '…' : '';

          toast.error(
            failedMessages.length === 1
              ? `Failed to create pair: ${errorPreview}`
              : `Failed to create ${failedMessages.length} pairs: ${errorPreview}${errorSuffix}`,
          );
        }

        if (createdPairs.length > 0) {
          onClose();
        }

        setLoading(false);
        return;
      }

      const _cex = selectedCexs?.[0]?.cex ?? 'gate';

      // Original logic for other DEXs
      const { isSuccess, data, errorMessage } = await ApiPairs.addPair({
        id: projectId,
        dex: _dex,
        cex: _cex,
        tradingType,
        fee: _fee,
        notes,
        token_base: baseToken,
        token_quote: quoteToken,
        swap_gas_limit: 1000000,
        pool_id: isAddress(pairAddress) ? pairAddress : undefined,
        pair: isAddress(pairAddress) ? pairAddress : undefined,
      });

      if (!isSuccess) {
        onClose();
        setLoading(false);
        dispatch(
          setAlertState({
            type: 'failed-img',
            text: errorMessage,
            onClose: () => dispatch(dropAlertState()),
            onSubmit: () => {
              dispatch(dropAlertState());
              onOpen();
            },
          }),
        );
        return;
      }

      if (isSuccess) {
        const isDex = tradingType === EExchange.dex;

        const { isSuccess, data: pairData } = isDex
          ? await ApiPairs.getDexPairById(Number(data.id))
          : await ApiCexPairs.getCexPairById(Number(data.id));

        onClose();
        setLoading(false);

        if (isSuccess) {
          dispatch(
            addNewPairToProject({
              pair: pairData,
              type: tradingType,
            }),
          );
          dispatch(
            setAlertState({
              type: 'success-img',
              text: `Created new pair: ${pairData.symbol}`,
              onClose: () => dispatch(dropAlertState()),
              onSubmit: () => {
                dispatch(dropAlertState());
              },
            }),
          );
          void refreshProjectCaches();
          navigate(
            generatePath(isDex ? ROUTE_PATHS.dexPair : ROUTE_PATHS.cexPair, {
              projectId: projectId.toString(),
              pairId: pairData.id.toString(),
            }),
          );
        }
      }
    },
    [
      navigate,
      projectId,
      dispatch,
      onClose,
      onOpen,
      tradingType,
      dex,
      isPancakeInfinite,
      refreshProjectCaches,
    ],
  );

  const { values, touched, errors, setFieldValue, handleSubmit } = useFormik<IForm>({
    initialValues: INITIAL_VALUES,
    validationSchema: validationSchema,
    onSubmit: addPair,
    enableReinitialize: true,
  });

  const pairAddress = useDeferredValue(values.pairAddress);

  useEffect(() => {
    poolResolveRequest.current += 1;
    setResolvedPool(null);
    setPoolResolveLoading(false);
    setPoolResolveError(undefined);
  }, [values.pairAddress]);

  // Pancake Infinite CL pool data fetching
  const {
    poolKey,
    extra: pancakeExtra,
    isLoading: isPancakePoolLoading,
    error: pancakePoolError,
  } = usePancakePoolData({
    poolId: values.poolId,
    enabled: isPancakeInfinite && values.poolId.length > 0,
  });

  // Store pancakeExtra in ref for use in addPair callback
  useEffect(() => {
    pancakeExtraRef.current = pancakeExtra;
  }, [pancakeExtra]);

  const setFormField = useCallback(
    (key: keyof IForm, value: any) => {
      if (loading) return;

      setEdited(true);
      setFieldValue(key, value);
    },
    [loading, setFieldValue],
  );

  useDebounce(
    () => {
      const request = ++poolResolveRequest.current;
      const shouldResolvePool =
        tradingType === EExchange.dex &&
        !isPancakeInfinite &&
        dex !== undefined &&
        isAddress(pairAddress);

      if (!shouldResolvePool) {
        setResolvedPool(null);
        setPoolResolveLoading(false);
        setPoolResolveError(undefined);
        return;
      }

      setResolvedPool(null);
      setPoolResolveLoading(true);
      setPoolResolveError(undefined);

      void ApiPairs.describeDexPair({
        id: projectId,
        dex: dex.dex,
        pair: pairAddress,
      })
        .then(response => {
          if (request !== poolResolveRequest.current) return;

          if (!response.isSuccess) {
            setPoolResolveError(response.errorMessage || 'Cannot describe pool');
            return;
          }

          const [baseToken, quoteToken] = response.data.tokens;
          if (!baseToken || !quoteToken) {
            setPoolResolveError('Pool response is missing tokens');
            return;
          }

          setResolvedPool(response.data);
          setFormField('baseToken', baseToken.addr);
          setFormField('quoteToken', quoteToken.addr);
          setFormField('fee', response.data.extra?.tickSpacing ?? response.data.fee);
        })
        .catch(() => {
          if (request === poolResolveRequest.current) {
            setPoolResolveError('Cannot describe pool');
          }
        })
        .finally(() => {
          if (request === poolResolveRequest.current) {
            setPoolResolveLoading(false);
          }
        });
    },
    350,
    [dex, isPancakeInfinite, pairAddress, projectId, setFormField, tradingType],
  );

  // Auto-fill tokens from Pancake Infinite pool ID
  useDebounce(
    () => {
      if (!isPancakeInfinite) {
        return;
      }

      // If pool ID is empty or invalid (no poolKey), clear the token fields
      if (!poolKey) {
        if (values.baseToken || values.quoteToken) {
          setFormField('baseToken', '');
          setFormField('quoteToken', '');
        }
        return;
      }

      const { currency0, currency1 } = poolKey;

      // Only auto-fill if both tokens are empty or if they don't match either direction
      const tokensAreEmpty = !values.baseToken && !values.quoteToken;
      const tokensMatchNormalOrder =
        values.baseToken === currency0 && values.quoteToken === currency1;
      const tokensMatchSwappedOrder =
        values.baseToken === currency1 && values.quoteToken === currency0;

      if (tokensAreEmpty) {
        // Auto-fill both tokens
        if (currency0) setFormField('baseToken', currency0);
        if (currency1) setFormField('quoteToken', currency1);
      } else if (!tokensMatchNormalOrder && !tokensMatchSwappedOrder) {
        // Tokens were changed to something else, reset to default order
        if (currency0) setFormField('baseToken', currency0);
        if (currency1) setFormField('quoteToken', currency1);
      }
      // If tokens match either order, don't touch them (preserves manual swaps)
    },
    300,
    [poolKey, isPancakeInfinite, values.baseToken, values.quoteToken, values.poolId, setFormField],
  );

  useEffect(() => {
    if (tradingType === EExchange.cex) {
      if (!cexs) {
        dispatch(getCexs());
      }
    }
  }, [tradingType, cexs, dispatch]);

  const pairParsingLoading = poolResolveLoading || isPancakePoolLoading;
  const pairParsingError = poolResolveError ?? pancakePoolError?.message;

  return {
    cexs: pairCexs,
    values,
    errors,
    touched,
    setFormField,
    handleSubmit,
    loading,
    pairParsingLoading,
    pairParsingError,
    edited,
    isPancakeInfinite,
    pancakeExtra,
    resolvedPool,
  };
};

export { useAddPairModal };
